import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Divider,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Code,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import {
  FaBook,
  FaNetworkWired,
  FaCoins,
  FaCode,
  FaServer,
  FaCheckCircle
} from 'react-icons/fa';
import { GiPokerHand } from 'react-icons/gi';
import { MdSecurity } from 'react-icons/md';

export const Block52Glossary = () => {
  const { colorMode } = useColorMode();
  const [searchTerm, setSearchTerm] = useState('');

  const glossarySections = [
    {
      id: 'core',
      icon: FaBook,
      title: 'Block52 Core Concepts',
      color: '#3b82f6',
      terms: [
        {
          term: 'Block52',
          definition: 'The decentralized poker platform ecosystem, including the blockchain, game engine, and supporting infrastructure.'
        },
        {
          term: 'Poker Virtual Machine (PVM)',
          definition: 'The Layer 2 game engine that executes poker game logic. Written in TypeScript, it handles game state management, card dealing and shuffling, action validation, pot calculations, and player management.'
        },
        {
          term: 'Card Virtual Machine (CVM)',
          definition: 'The conceptual virtual card game machine that runs on the Block52 network. The PVM runs inside the CVM and handles poker-specific logic.'
        },
        {
          term: 'Pokerchain',
          definition: 'The Layer 1 Cosmos SDK blockchain that provides state consensus, transaction ordering, token management, bridge integration, and validator network.'
        },
        {
          term: 'Club in a Box',
          definition: 'A white-label solution allowing operators to deploy their own branded poker platform using Block52 infrastructure.'
        },
        {
          term: 'Agent',
          definition: 'An operator or affiliate who runs a poker club using Block52 infrastructure. Agents can customize branding and manage their own player base.'
        }
      ]
    },
    {
      id: 'blockchain',
      icon: FaNetworkWired,
      title: 'Blockchain & Cosmos Terms',
      color: '#64ffda',
      terms: [
        {
          term: 'Cosmos SDK',
          definition: 'The blockchain framework used to build Pokerchain. Provides modular components for building application-specific blockchains.'
        },
        {
          term: 'CometBFT',
          definition: 'The consensus engine (formerly Tendermint) that provides Byzantine Fault Tolerant consensus for Pokerchain.'
        },
        {
          term: 'IBC (Inter-Blockchain Communication)',
          definition: 'Protocol allowing Pokerchain to communicate with other Cosmos chains. Enables cross-chain token transfers.'
        },
        {
          term: 'Module',
          definition: 'A self-contained piece of blockchain functionality. Pokerchain\'s custom x/poker module handles game logic.'
        },
        {
          term: 'Keeper',
          definition: 'The interface to a module\'s state store. Handles reading and writing blockchain state for a specific module.'
        },
        {
          term: 'Collections Framework',
          definition: 'Modern Cosmos SDK pattern for state management using typed collections (Maps, KeySets, Items) instead of raw key-value stores.'
        },
        {
          term: 'Validator',
          definition: 'A node that participates in block production and consensus. Validators stake tokens and earn rewards for securing the network.'
        },
        {
          term: 'Delegator',
          definition: 'Someone who stakes tokens to a validator to help secure the network, earning a share of validator rewards.'
        },
        {
          term: 'Staking',
          definition: 'Locking tokens to participate in network consensus or delegate to validators. Required for validator operation.'
        },
        {
          term: 'Slashing',
          definition: 'Penalty mechanism where validators lose staked tokens for malicious behavior or extended downtime.'
        },
        {
          term: 'RPC (Remote Procedure Call)',
          definition: 'The interface for querying blockchain data and broadcasting transactions. Default port: 26657.'
        },
        {
          term: 'REST API',
          definition: 'HTTP-based API for blockchain queries. Default port: 1317. Easier to use than RPC from web applications.'
        },
        {
          term: 'gRPC',
          definition: 'High-performance RPC protocol used for blockchain communication. Default port: 9090.'
        },
        {
          term: 'Chain ID',
          definition: 'Unique identifier for a blockchain network. Pokerchain uses pokerchain as its chain ID.'
        },
        {
          term: 'Address Prefix',
          definition: 'The human-readable prefix for blockchain addresses. Pokerchain uses b52 (e.g., b521rgaelup3yzxt6puf593k5wq3mz8k0m2pvkfj9p).'
        },
        {
          term: 'Genesis',
          definition: 'The initial state of the blockchain. Genesis file (genesis.json) defines starting accounts, parameters, and configuration.'
        }
      ]
    },
    {
      id: 'poker',
      icon: GiPokerHand,
      title: 'Poker Game Terms',
      color: '#3b82f6',
      terms: [
        {
          term: 'Texas Hold\'em',
          definition: 'The primary poker variant supported by Block52. Players receive 2 hole cards and share 5 community cards.'
        },
        {
          term: 'Sit & Go (SNG)',
          definition: 'Tournament format that starts when a specific number of players register. No scheduled start time.'
        },
        {
          term: 'Multi-Table Tournament (MTT)',
          definition: 'Tournament spanning multiple tables with scheduled start time and progressive blind increases.'
        },
        {
          term: 'Hole Cards',
          definition: 'The private cards dealt to each player (2 in Texas Hold\'em).'
        },
        {
          term: 'Community Cards',
          definition: 'Shared cards in the center of the table. Texas Hold\'em uses 5 community cards.'
        },
        {
          term: 'Flop',
          definition: 'The first three community cards revealed simultaneously.'
        },
        {
          term: 'Turn',
          definition: 'The fourth community card (also called "Fourth Street").'
        },
        {
          term: 'River',
          definition: 'The fifth and final community card (also called "Fifth Street").'
        },
        {
          term: 'Showdown',
          definition: 'Final phase where remaining players reveal cards to determine the winner.'
        },
        {
          term: 'Pot',
          definition: 'The total amount of chips/tokens bet by all players in a hand.'
        },
        {
          term: 'Side Pot',
          definition: 'Additional pot created when a player is all-in and other players continue betting.'
        },
        {
          term: 'Blinds',
          definition: 'Forced bets to initiate action. Small blind and big blind rotate each hand.'
        },
        {
          term: 'Button',
          definition: 'Marker indicating the dealer position. Rotates clockwise each hand.'
        },
        {
          term: 'Ante',
          definition: 'Optional forced bet from all players before cards are dealt.'
        }
      ]
    },
    {
      id: 'technical',
      icon: FaCode,
      title: 'Technical Architecture',
      color: '#64ffda',
      terms: [
        {
          term: 'Layer 1 (L1)',
          definition: 'The base blockchain layer (Pokerchain). Handles consensus, state, and transactions.'
        },
        {
          term: 'Layer 2 (L2)',
          definition: 'Application layer built on top of L1 (Poker VM). Handles game logic and user interface.'
        },
        {
          term: 'WebSocket',
          definition: 'Real-time bidirectional communication protocol. Used for live game updates between PVM and UI.'
        },
        {
          term: 'Game State',
          definition: 'The complete current state of a poker game, including players and their chip stacks, current betting round, community cards, pot size, and active player turn.'
        },
        {
          term: 'Game ID',
          definition: 'Unique identifier for each poker game instance.'
        },
        {
          term: 'Table ID',
          definition: 'Identifier for a poker table (which may host multiple sequential games).'
        },
        {
          term: 'Session',
          definition: 'A player\'s connection to a table, tracked via WebSocket.'
        },
        {
          term: 'Nonce',
          definition: 'Transaction counter to prevent replay attacks. Each account transaction must have sequential nonce.'
        },
        {
          term: 'Protobuf (Protocol Buffers)',
          definition: 'Binary serialization format used for blockchain messages and state. Defined in .proto files.'
        },
        {
          term: 'TypeScript Client',
          definition: 'Auto-generated SDK from protobuf definitions. Located in ts-client/ directory.'
        },
        {
          term: 'CosmJS',
          definition: 'JavaScript library for interacting with Cosmos SDK blockchains. Used in Block52 SDK.'
        }
      ]
    },
    {
      id: 'tokens',
      icon: FaCoins,
      title: 'Tokens & Economics',
      color: '#3b82f6',
      terms: [
        {
          term: 'B52 Token',
          definition: 'The native staking and governance token of Pokerchain. Used for validator staking, gas fees, and governance voting. Total supply: 52 million tokens.'
        },
        {
          term: 'b52USDC',
          definition: 'USDC stablecoin bridged to Pokerchain from Ethereum. Used for playing poker, tournament buy-ins, and cash game stakes.'
        },
        {
          term: 'USDC (USD Coin)',
          definition: 'Stablecoin pegged to US dollar. Locked on Ethereum, minted as b52USDC on Pokerchain.'
        },
        {
          term: 'Stake',
          definition: 'General term for tokens locked in validator delegation or betting chips in poker games.'
        },
        {
          term: 'Gas Fees',
          definition: 'Transaction costs paid to validators for processing blockchain transactions. Denominated in B52 tokens.'
        },
        {
          term: 'Rake',
          definition: 'Percentage of pot taken by platform as fee.'
        },
        {
          term: 'Buy-In',
          definition: 'Amount of tokens required to enter a tournament or sit at a cash game table.'
        }
      ]
    },
    {
      id: 'infrastructure',
      icon: FaServer,
      title: 'Network & Infrastructure',
      color: '#64ffda',
      terms: [
        {
          term: 'Mainnet',
          definition: 'Production blockchain network. Block52\'s primary network.'
        },
        {
          term: 'Testnet',
          definition: 'Testing blockchain network for development and testing purposes.'
        },
        {
          term: 'Localhost',
          definition: 'Local development environment running on developer\'s machine.'
        },
        {
          term: 'Persistent Peers',
          definition: 'Nodes configured to maintain constant connections for blockchain sync and gossip.'
        },
        {
          term: 'Seed Nodes',
          definition: 'Nodes that help new nodes discover peers in the network.'
        },
        {
          term: 'Moniker',
          definition: 'Human-readable name for a validator node (e.g., "block52", "texashodl").'
        },
        {
          term: 'Node ID',
          definition: 'Unique identifier for a blockchain node, derived from node key.'
        },
        {
          term: 'Faucet',
          definition: 'Service that distributes free testnet tokens for development.'
        },
        {
          term: 'Explorer',
          definition: 'Blockchain data viewer showing blocks, transactions, and state.'
        }
      ]
    }
  ];

  const playerActions = [
    { action: 'Fold', description: 'Discard cards and forfeit the hand' },
    { action: 'Check', description: 'Pass action without betting (only if no bet to call)' },
    { action: 'Call', description: 'Match the current bet' },
    { action: 'Bet', description: 'Place a wager (when no bet exists)' },
    { action: 'Raise', description: 'Increase the current bet' },
    { action: 'All-In', description: 'Bet all remaining chips' }
  ];

  const playerStatuses = [
    { status: 'SEATED', description: 'Joined but not yet active' },
    { status: 'ACTIVE', description: 'Participating in current hand' },
    { status: 'FOLDED', description: 'Folded current hand' },
    { status: 'ALL_IN', description: 'Bet all chips' },
    { status: 'SITTING_OUT', description: 'Temporarily away' },
    { status: 'SITTING_IN', description: 'Returning from sitting out' },
    { status: 'BUSTED', description: 'No chips remaining' },
    { status: 'SHOWING', description: 'Revealing cards at showdown' }
  ];

  const commonAcronyms = [
    { acronym: 'PVM', full: 'Poker Virtual Machine', description: 'Layer 2 game engine' },
    { acronym: 'CVM', full: 'Card Virtual Machine', description: 'Virtual card game machine concept' },
    { acronym: 'RPC', full: 'Remote Procedure Call', description: 'Blockchain query interface' },
    { acronym: 'REST', full: 'Representational State Transfer', description: 'HTTP API interface' },
    { acronym: 'IBC', full: 'Inter-Blockchain Communication', description: 'Cross-chain protocol' },
    { acronym: 'SDK', full: 'Software Development Kit', description: 'Developer tools and libraries' },
    { acronym: 'SNG', full: 'Sit & Go', description: 'Tournament format' },
    { acronym: 'MTT', full: 'Multi-Table Tournament', description: 'Large tournament format' },
    { acronym: 'USDC', full: 'USD Coin', description: 'Stablecoin' }
  ];

  // Filter function
  const filterTerms = (terms: Array<{ term: string; definition: string }>) => {
    if (!searchTerm) return terms;
    return terms.filter(
      item =>
        item.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.definition.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="1200px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Block52 Glossary
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                Your complete guide to Block52 terminology, blockchain poker concepts, and technical definitions.
              </Text>
            </VStack>

            {/* Search Bar */}
            <Box w="full">
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={getColor('text.secondary', colorMode)} />
                </InputLeftElement>
                <Input
                  placeholder="Search terms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.300'}
                  _hover={{
                    borderColor: '#3b82f6',
                  }}
                  _focus={{
                    borderColor: '#3b82f6',
                    boxShadow: '0 0 0 1px #3b82f6',
                  }}
                />
              </InputGroup>
            </Box>

            <Divider />

            {/* Glossary Sections */}
            <Accordion allowMultiple w="full" defaultIndex={[0]}>
              {glossarySections.map((section) => {
                const filteredTerms = filterTerms(section.terms);
                if (searchTerm && filteredTerms.length === 0) return null;

                return (
                  <AccordionItem
                    key={section.id}
                    border="none"
                    mb={4}
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    <AccordionButton
                      p={6}
                      _hover={{
                        bg: colorMode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'gray.50'
                      }}
                    >
                      <Icon as={section.icon} color={section.color} boxSize={6} mr={4} />
                      <Heading
                        size="md"
                        flex="1"
                        textAlign="left"
                        color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
                      >
                        {section.title}
                        {searchTerm && filteredTerms.length > 0 && (
                          <Badge ml={3} colorScheme="blue">
                            {filteredTerms.length} match{filteredTerms.length !== 1 ? 'es' : ''}
                          </Badge>
                        )}
                      </Heading>
                      <AccordionIcon color={section.color} />
                    </AccordionButton>
                    <AccordionPanel pb={6} px={6}>
                      <VStack spacing={4} align="start">
                        {filteredTerms.map((item, index) => (
                          <Box key={index} w="full">
                            <Heading
                              size="sm"
                              color={section.color}
                              mb={2}
                            >
                              {item.term}
                            </Heading>
                            <Text
                              color={getColor('text.secondary', colorMode)}
                              lineHeight="tall"
                            >
                              {item.definition}
                            </Text>
                            {index < filteredTerms.length - 1 && <Divider mt={4} />}
                          </Box>
                        ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {/* Player Actions Reference */}
            <Box
              w="full"
              p={6}
              bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
              borderRadius="lg"
              border="1px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
            >
              <Heading
                size="md"
                mb={4}
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Player Actions
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {playerActions.map((item, index) => (
                  <Box key={index}>
                    <Text fontWeight="bold" color="#3b82f6">
                      {item.action}
                    </Text>
                    <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                      {item.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Player Statuses Reference */}
            <Box
              w="full"
              p={6}
              bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
              borderRadius="lg"
              border="1px solid"
              borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
            >
              <Heading
                size="md"
                mb={4}
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Player Statuses
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {playerStatuses.map((item, index) => (
                  <Box key={index}>
                    <Code colorScheme="blue" fontSize="sm" fontWeight="bold">
                      {item.status}
                    </Code>
                    <Text fontSize="sm" color={getColor('text.secondary', colorMode)} mt={1}>
                      {item.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Common Acronyms */}
            <Box
              w="full"
              p={6}
              bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
              borderRadius="lg"
              border="1px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
            >
              <Heading
                size="md"
                mb={4}
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Common Acronyms
              </Heading>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={getColor('text.secondary', colorMode)}>Acronym</Th>
                      <Th color={getColor('text.secondary', colorMode)}>Full Term</Th>
                      <Th color={getColor('text.secondary', colorMode)}>Description</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {commonAcronyms.map((item, index) => (
                      <Tr key={index}>
                        <Td>
                          <Code colorScheme="blue" fontWeight="bold">
                            {item.acronym}
                          </Code>
                        </Td>
                        <Td color={getColor('text.secondary', colorMode)}>{item.full}</Td>
                        <Td color={getColor('text.secondary', colorMode)}>{item.description}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>

            {/* Additional Concepts */}
            <Box
              w="full"
              p={6}
              bgGradient={
                colorMode === 'dark'
                  ? 'linear(to-r, rgba(59, 130, 246, 0.1), rgba(100, 255, 218, 0.1))'
                  : 'linear(to-r, blue.50, teal.50)'
              }
              borderRadius="xl"
              border="2px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
            >
              <Heading
                size="md"
                mb={4}
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Additional Concepts
              </Heading>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#64ffda" />
                  <Text as="span" fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Byzantine Fault Tolerance:{' '}
                  </Text>
                  <Text as="span" color={getColor('text.secondary', colorMode)}>
                    Ability of blockchain to reach consensus even if some nodes are malicious or faulty
                  </Text>
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#64ffda" />
                  <Text as="span" fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Block Time:{' '}
                  </Text>
                  <Text as="span" color={getColor('text.secondary', colorMode)}>
                    Time between blocks. Pokerchain targets ~6 seconds
                  </Text>
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#64ffda" />
                  <Text as="span" fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    White-Label:{' '}
                  </Text>
                  <Text as="span" color={getColor('text.secondary', colorMode)}>
                    Customizing the Block52 UI with your own branding, colors, and domain
                  </Text>
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#64ffda" />
                  <Text as="span" fontWeight="bold" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Double Spend Prevention:{' '}
                  </Text>
                  <Text as="span" color={getColor('text.secondary', colorMode)}>
                    Security measure preventing same tokens from being spent twice
                  </Text>
                </ListItem>
              </List>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default Block52Glossary;
